import { Component, Injector, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AbstractComponent,
  Attendance,
  BANT_FACT_LABEL,
  BANT_FACTS,
  BantFact,
  Card,
  CardService,
  Stage,
  StageKindEnum,
  StageService,
  TASK_OUTCOME_LABEL,
  Task,
  TaskOutcomeEnum,
  TaskStatusEnum,
  TaskTypeEnum,
  TasksService,
  URGENCY_CONTEXT,
  URGENCY_ICON,
  URGENCY_LABEL,
  taskTypeLabelKey,
} from 'lib-trend-core';
import { finalize, Subscription, takeUntil } from 'rxjs';
import { CardFormComponent } from '../../../../kanban/card-form/card-form.component';
import { NextActionDialogComponent } from '../../../../kanban/next-action-dialog/next-action-dialog.component';
import { PresentationDateDialogComponent, PresentationDateDialogResult } from '../../../../kanban/presentation-date-dialog/presentation-date-dialog.component';
import { SelectLossReasonComponent, SelectLossReasonResult } from '../../../../kanban/loss-reason/select-loss-reason/select-loss-reason.component';
import { ScoreExplanationComponent } from '../../../../kanban/score-explanation/score-explanation.component';
import { ConcludeActivityDialogComponent, ConcludeActivityDialogResult } from '../../../../task/activity-dialogs/conclude-activity-dialog/conclude-activity-dialog.component';
import { PostponeActivityDialogComponent } from '../../../../task/activity-dialogs/postpone-activity-dialog/postpone-activity-dialog.component';

const ACTIVITY_ICON: Partial<Record<TaskTypeEnum, string>> = {
  [TaskTypeEnum.CALL]: 'ri-phone-line',
  [TaskTypeEnum.MEETING]: 'ri-team-line',
  [TaskTypeEnum.PRESENTATION]: 'ri-slideshow-line',
  [TaskTypeEnum.APPOINTMENT]: 'ri-calendar-event-line',
  [TaskTypeEnum.FOLLOW_UP]: 'ri-chat-3-line',
  [TaskTypeEnum.VISIT]: 'ri-map-pin-line',
};

/** The activity bullet is coloured by what the interaction produced, not by its type. */
const OUTCOME_CONTEXT: Partial<Record<TaskOutcomeEnum, string>> = {
  [TaskOutcomeEnum.HELD]: 'success',
  [TaskOutcomeEnum.PROPOSAL_SENT]: 'success',
  [TaskOutcomeEnum.RESCHEDULED]: 'warning',
  [TaskOutcomeEnum.NO_ANSWER]: 'danger',
  [TaskOutcomeEnum.NOT_INTERESTED]: 'danger',
};

/**
 * Opportunity tab of the attendance panel: every deal of the conversation's contact,
 * the one opened from this attendance first and expanded, each item carrying its own
 * next action. Every action reuses the CRM dialogs, and nothing here computes a
 * signal — the API resolves them (RNF-02).
 */
@Component({
  selector: 'attendance-opportunity-tab',
  templateUrl: './attendance-opportunity-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AttendanceOpportunityTabComponent extends AbstractComponent implements OnChanges {

  @Input() attendance!: Attendance;

  cards: Array<Card> = [];
  loadingCards = false;
  busy = false;

  readonly bantFacts = BANT_FACTS;
  readonly BANT_FACT_LABEL = BANT_FACT_LABEL;

  private readonly expanded = new Set<string>();
  private readonly stagesByFunnel = new Map<string, Array<Stage>>();
  private readonly activitiesByCard = new Map<string, Array<Task>>();
  private loadSubscription?: Subscription;

  constructor(
    injector: Injector,
    private readonly dialog: MatDialog,
    private readonly cardService: CardService,
    private readonly stageService: StageService,
    private readonly tasksService: TasksService,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The panel passes a newer copy of the same attendance after each update: what the user opened stays open.
    if (changes['attendance']?.previousValue?._id === this.attendance?._id) return;
    // Another attendance: the previous one's deals leave the screen now, not when the new list arrives.
    this.cards = [];
    this.expanded.clear();
    this.activitiesByCard.clear();
    this.load();
  }

  load(): void {
    // Only the newest read counts: one still running (another attendance, an older refresh) is dropped.
    this.loadSubscription?.unsubscribe();

    const idContact = this.idOf(this.attendance?.contact);
    if (!idContact) {
      this.cards = [];
      return;
    }

    this.loadingCards = true;
    this.loadSubscription = this.cardService.getCardsByContact(idContact, this.attendance._id)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.loadingCards = false)))
      .subscribe({
        next: (cards) => {
          this.cards = cards ?? [];
          // The first load opens the deal of this attendance; a refresh keeps what the user opened.
          if (!this.expanded.size) {
            const own = this.cards.find((card) => this.isOfAttendance(card));
            if (own) this.expanded.add(own._id);
          }
          this.cards.filter((card) => this.isExpanded(card)).forEach((card) => this.loadDetails(card));
        },
        error: (err) => this.alertService.error(err?.error?.message ?? this.t('attendance.opportunityTab.loadError')),
      });
  }

  isOfAttendance(card: Card): boolean {
    return this.idOf(card?.attendance) === this.attendance?._id;
  }

  isExpanded(card: Card): boolean {
    return this.expanded.has(card._id);
  }

  toggle(card: Card): void {
    if (this.isExpanded(card)) {
      this.expanded.delete(card._id);
      return;
    }
    this.expanded.add(card._id);
    this.loadDetails(card);
  }

  isClosed(card: Card): boolean {
    return card?.won !== null && card?.won !== undefined;
  }

  closedAt(card: Card): Date | undefined {
    return card?.closedAt ?? (card?.won ? card?.winDate : card?.lostDate);
  }

  /** The steps the deal walks through — a lost stage is an outcome, not a step. */
  stagesOf(card: Card): Array<Stage> {
    return this.allStagesOf(card).filter((stage) => !stage.lostDeal);
  }

  wonStageOf(card: Card): Stage | undefined {
    return this.allStagesOf(card).find((stage) => stage.wonDeal);
  }

  lostStageOf(card: Card): Stage | undefined {
    return this.allStagesOf(card).find((stage) => stage.lostDeal);
  }

  stageIndex(card: Card): number {
    return this.stagesOf(card).findIndex((stage) => stage._id === this.idOf(card?.stage));
  }

  nextStage(card: Card): Stage | undefined {
    if (this.isClosed(card)) return undefined;
    const index = this.stageIndex(card);
    return index < 0 ? undefined : this.stagesOf(card)[index + 1];
  }

  activitiesOf(card: Card): Array<Task> {
    return this.activitiesByCard.get(card._id) ?? [];
  }

  activityTypeLabel(task: Task): string {
    return taskTypeLabelKey(task?.type);
  }

  activityOutcomeLabel(task: Task): string {
    return TASK_OUTCOME_LABEL[task?.outcome] ?? 'kanban.taskOutcome.other';
  }

  activityIcon(task: Task): string {
    return ACTIVITY_ICON[task?.type] ?? 'ri-checkbox-circle-line';
  }

  activityContext(task: Task): string {
    return OUTCOME_CONTEXT[task?.outcome] ?? (this.isConcluded(task) ? 'success' : 'secondary');
  }

  isConcluded(task: Task): boolean {
    return !!task?.outcome || task?.status === TaskStatusEnum.DONE;
  }

  /** The task behind `card.nextAction`, from the loaded activities or reduced to what the deal carries. */
  nextActionTask(card: Card): Task | undefined {
    const idTask = card?.nextAction?.task;
    if (!idTask) return undefined;
    return this.activitiesOf(card).find((activity) => activity._id === idTask)
      ?? ({ _id: idTask, type: card.nextAction.type } as Task);
  }

  /** The task's own name — unless it is the API's fallback (the type key itself), which reads as the type label. */
  nextActionLabel(card: Card): string {
    const type = card?.nextAction?.type;
    const name = this.nextActionTask(card)?.name?.trim();
    return name && name !== type ? name : this.t(taskTypeLabelKey(type));
  }

  /** "Vencida há N dias" once a whole day has passed; otherwise the radar's own state word. */
  nextActionState(card: Card): { key: string; params?: Record<string, unknown> } {
    const due = new Date(card?.nextAction?.dueAt).getTime();
    const days = card?.urgency === 'OVERDUE' && Number.isFinite(due) ? Math.floor((Date.now() - due) / 86_400_000) : 0;

    if (days > 1) return { key: 'attendance.opportunityTab.overdueDays', params: { days } };
    if (days === 1) return { key: 'attendance.opportunityTab.overdueDay' };
    return { key: URGENCY_LABEL[card?.urgency] ?? 'kanban.urgency.scheduled' };
  }

  urgencyIcon(card: Card): string {
    return URGENCY_ICON[card?.urgency] ?? 'ri-calendar-check-line';
  }

  urgencyContext(card: Card): string {
    return URGENCY_CONTEXT[card?.urgency] ?? 'secondary';
  }

  bantDone(card: Card): number {
    return this.bantFacts.filter((fact) => card?.bant?.[fact] === true).length;
  }

  toggleBant(card: Card, fact: BantFact): void {
    if (this.busy) return;

    this.busy = true;
    this.cardService.setBant(card._id, { [fact]: card?.bant?.[fact] !== true })
      .pipe(takeUntil(this.destroy$), finalize(() => (this.busy = false)))
      .subscribe({
        next: (saved) => (card.bant = saved?.bant),
        error: (err) => this.alertService.error(err?.error?.message ?? this.t('kanban.bant.saveError')),
      });
  }

  /** Same gate the board applies on a drop (E2 RF-15): a presentation stage offers the date first. */
  advance(card: Card): void {
    const target = this.nextStage(card);
    if (!target || this.busy) return;

    if (target.kind === StageKindEnum.PRESENTATION && !card.presentedAt) {
      this.dialog.open(PresentationDateDialogComponent, { width: '560px', disableClose: true, data: { card } })
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: PresentationDateDialogResult | undefined) => {
          // Only a cancel keeps the deal where it is; skipping moves it without a date.
          if (result) this.moveTo(card, target, 'presentedAt' in result ? result.presentedAt : undefined);
        });
      return;
    }

    this.moveTo(card, target);
  }

  markWon(card: Card): void {
    const target = this.wonStageOf(card);
    if (target && !this.busy) this.moveTo(card, target);
  }

  /** A lost deal needs its reason first (RF-29) — the board's own dialog, platform discards included. */
  markLost(card: Card): void {
    const target = this.lostStageOf(card);
    if (!target || this.busy) return;

    this.dialog.open(SelectLossReasonComponent, { width: '700px', disableClose: true, data: { card } })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: SelectLossReasonResult | undefined) => {
        if (result?.idLossReason) this.moveTo(card, target, undefined, result.idLossReason, result.lossNote);
      });
  }

  openNextAction(card: Card, chainedFromOutcome = false): void {
    this.dialog.open(NextActionDialogComponent, { data: { card, chainedFromOutcome }, width: '640px' })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((saved) => {
        if (saved) this.load();
      });
  }

  openScoreExplanation(card: Card): void {
    this.dialog.open(ScoreExplanationComponent, { data: { card }, width: '720px' });
  }

  /**
   * E4 RF-17: the deal's next action is concluded through the ONE conclusion dialog — outcome,
   * moment, duration, notes, an optional move and the next step, in one submit. The deal is
   * reloaded afterwards: its signals are recomputed server-side.
   */
  concludeNextAction(card: Card): void {
    const idTask = card?.nextAction?.task;
    if (!idTask) return;

    this.dialog.open(ConcludeActivityDialogComponent, { data: { idTask, card }, width: '720px', disableClose: true })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: ConcludeActivityDialogResult | undefined) => {
        if (result) this.load();
      });
  }

  /** E4 RF-24: postpones the deal's next action with a reason. */
  postponeNextAction(card: Card): void {
    const task = this.nextActionTask(card);
    if (!task?._id) return;

    const withMoment: Task = { ...task, deadlineAt: task.deadlineAt ?? card.nextAction?.dueAt, untimed: task.untimed ?? card.nextAction?.untimed };
    this.dialog.open(PostponeActivityDialogComponent, { data: { task: withMoment }, width: '560px' })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((postponed: Task | undefined) => {
        if (postponed) this.load();
      });
  }

  /** `d MMM` for a commitment with no set time (E4 RF-05); the time otherwise. */
  nextActionFormat(card: Card): string {
    return card?.nextAction?.untimed === true ? 'd MMM' : 'd MMM, HH:mm';
  }

  /** The same rule for a listed activity. */
  activityFormat(task: Task): string {
    return task?.untimed === true && !task?.completedAt ? 'd MMM' : 'd MMM, HH:mm';
  }

  convert(): void {
    this.dialog.open(CardFormComponent, { width: '900px', data: { attendance: this.attendance } })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((card: Card) => {
        if (card) this.goToKanban(card);
      });
  }

  /** Opens the deal itself, through the board's deep link (`?card=`) — the board never reads a funnel from the URL. */
  goToKanban(card: Card): void {
    this.router.navigate(['/kanban'], { queryParams: { card: card._id } });
  }

  private moveTo(card: Card, stage: Stage, presentedAt?: string, idLossReason?: string, lossNote?: string): void {
    this.busy = true;
    this.cardService.updateCardStage(card._id, stage._id, this.getCurrentUser(), idLossReason, presentedAt, lossNote)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.busy = false)))
      .subscribe({
        next: () => {
          this.alertService.success(this.t('kanban.stageColumn.cardMovedSuccess'));
          this.load();
        },
        // A refusal from the API (BANT gate) arrives translated; the generic text is the fallback.
        error: (err) => this.alertService.error(err?.error?.message ?? this.t('kanban.stageColumn.errorMovingCard')),
      });
  }

  private loadDetails(card: Card): void {
    const idFunnel = this.idOf(card?.funnel);
    if (idFunnel && !this.stagesByFunnel.has(idFunnel)) {
      this.stageService.getStagesByFunnel(idFunnel)
        .pipe(takeUntil(this.destroy$))
        .subscribe((stages) => this.stagesByFunnel.set(
          idFunnel,
          (stages ?? []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        ));
    }

    this.tasksService.listByCard(card._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((activities) => this.activitiesByCard.set(card._id, activities ?? []));
  }

  private allStagesOf(card: Card): Array<Stage> {
    return this.stagesByFunnel.get(this.idOf(card?.funnel)) ?? [];
  }

  private idOf(value: unknown): string {
    return `${(value as { _id?: unknown })?._id ?? value ?? ''}`;
  }
}
